import React, { useEffect, useMemo, useState } from "react";
import {
    Dropdown,
    Form,
    Header,
    Icon,
    Input,
    Label,
    Loader,
    Menu,
    Message,
    Pagination,
    Segment,
    Table,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import { getRolePerformance } from "../../../services/reporting.service";
import {
    ReportFilters,
    ReportRole,
    RolePerformanceResponse,
    RolePerformanceMetric,
    SortDirection,
} from "../../../types/models/Reporting";
import {
    formatCurrency,
    formatNumber,
    formatPercent,
} from "../reportingFormatters";
import RolePerformanceDrilldownModal, {
    RolePerformanceSelection,
} from "../modals/RolePerformanceDrilldownModal";

interface Props {
    filters: ReportFilters;
    reloadToken: number;
}

const roles: Array<{ role: ReportRole; label: string; description: string; icon: any }> = [
    { role: "ADVISOR", label: "Asesores", description: "Embudo por cohorte de prospectos y pedidos asignados por fecha de creación", icon: "user" },
    { role: "SOCIAL_MEDIA", label: "Redes sociales", description: "Cohorte de prospectos creados en el periodo y resultado conocido al corte", icon: "bullhorn" },
    { role: "INSTALLER", label: "Instaladores", description: "Resultados de pedidos asignados; no representa acciones capturadas por el instalador", icon: "wrench" },
    { role: "ORDERS", label: "Pedidos", description: "Creación, validación, producción, tiempos y seguimiento atribuibles", icon: "clipboard list" },
    { role: "LOGISTIC", label: "Logística", description: "Transiciones, cierres, tiempos y backlog vigente al corte", icon: "truck" },
];

const defaultSort: Record<ReportRole, { property: string; direction: SortDirection }> = {
    ADVISOR: { property: "conversionRatePercent", direction: "desc" },
    SOCIAL_MEDIA: { property: "conversionRatePercent", direction: "desc" },
    INSTALLER: { property: "overdueInstallations", direction: "desc" },
    ORDERS: { property: "productionTransitions", direction: "desc" },
    LOGISTIC: { property: "scheduledTransitions", direction: "desc" },
};

const RolePerformance = ({ filters, reloadToken }: Props) => {
    const [role, setRole] = useState<ReportRole>("ADVISOR");
    const [data, setData] = useState<RolePerformanceResponse>();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [active, setActive] = useState<boolean | undefined>();
    const [sortProperty, setSortProperty] = useState(defaultSort.ADVISOR.property);
    const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSort.ADVISOR.direction);
    const [selection, setSelection] = useState<RolePerformanceSelection>();

    const roleConfig = useMemo(() => roles.find((item) => item.role === role) || roles[0], [role]);

    useEffect(() => {
        let mounted = true;
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await getRolePerformance(role, filters, {
                    page,
                    size,
                    sort: `${sortProperty},${sortDirection}`,
                    search,
                    active,
                    orderSearch: filters.search,
                });
                if (mounted) setData(response.data);
            } catch (error: any) {
                if (mounted) toast.error(error.message || "No fue posible consultar el desempeño.");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchData();
        return () => { mounted = false; };
    }, [role, filters, reloadToken, page, size, search, active, sortProperty, sortDirection]);

    const selectRole = (selectedRole: ReportRole) => {
        const fallback = defaultSort[selectedRole];
        setRole(selectedRole);
        setPage(0);
        setSearchInput("");
        setSearch("");
        setSortProperty(fallback.property);
        setSortDirection(fallback.direction);
    };

    const changeSort = (property: string) => {
        setPage(0);
        setSortDirection((current) => sortProperty === property && current === "asc" ? "desc" : "asc");
        setSortProperty(property);
    };

    const sorted = (property: string) => sortProperty === property
        ? (sortDirection === "asc" ? "ascending" : "descending")
        : undefined;

    const userCell = (user: RolePerformanceResponse["users"][number]) => (
        <div className="dashboard-user-cell">
            <strong>{user.fullName || user.username}</strong>
            <span>@{user.username} {!user.active && <Label size="mini">Inactivo</Label>}</span>
        </div>
    );

    const metricCell = (
        user: RolePerformanceResponse["users"][number],
        metric: RolePerformanceMetric,
        label: string,
        content: React.ReactNode,
        displayedValue: string,
        textAlign: "left" | "center" | "right" = "right"
    ) => (
        <Table.Cell textAlign={textAlign}>
            <button
                type="button"
                className="dashboard-metric-button"
                aria-label={`Ver ${label} de ${user.fullName || user.username}`}
                onClick={() => setSelection({
                    role,
                    userId: user.userId,
                    userName: user.fullName || user.username,
                    metric,
                    label,
                    displayedValue,
                })}
            >
                {content}
            </button>
        </Table.Cell>
    );

    const renderHeaders = () => {
        if (role === "ADVISOR") return (
            <Table.Row>
                <Table.HeaderCell sorted={sorted("fullName")} onClick={() => changeSort("fullName")}>Asesor</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("prospects")} onClick={() => changeSort("prospects")} textAlign="right">Prospectos asignados</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("convertedProspects")} onClick={() => changeSort("convertedProspects")} textAlign="right">Vendidos al corte</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("conversionRatePercent")} onClick={() => changeSort("conversionRatePercent")} textAlign="right">Conversión</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("averageDaysToSale")} onClick={() => changeSort("averageDaysToSale")} textAlign="right">Días a venta</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("activeOrders")} onClick={() => changeSort("activeOrders")} textAlign="right">Activos</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("attentionOrders")} onClick={() => changeSort("attentionOrders")} textAlign="right">Por atender</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("salesAmount")} onClick={() => changeSort("salesAmount")} textAlign="right">Valor pedidos creados</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("collectedAmount")} onClick={() => changeSort("collectedAmount")} textAlign="right">Cobrado acumulado</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("activeOutstandingBalance")} onClick={() => changeSort("activeOutstandingBalance")} textAlign="right">Saldo activo</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("collectionRatePercent")} onClick={() => changeSort("collectionRatePercent")} textAlign="right">Cobranza</Table.HeaderCell>
            </Table.Row>
        );
        if (role === "SOCIAL_MEDIA") return (
            <Table.Row>
                <Table.HeaderCell sorted={sorted("fullName")} onClick={() => changeSort("fullName")}>Usuario</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("prospects")} onClick={() => changeSort("prospects")} textAlign="right">Prospectos</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("convertedProspects")} onClick={() => changeSort("convertedProspects")} textAlign="right">Convertidos</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("lostProspects")} onClick={() => changeSort("lostProspects")} textAlign="right">Perdidos</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("openProspects")} onClick={() => changeSort("openProspects")} textAlign="right">Abiertos</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("conversionRatePercent")} onClick={() => changeSort("conversionRatePercent")} textAlign="right">Conversión</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("averageDaysToSale")} onClick={() => changeSort("averageDaysToSale")} textAlign="right">Días a venta</Table.HeaderCell>
            </Table.Row>
        );
        if (role === "INSTALLER") return (
            <Table.Row>
                <Table.HeaderCell sorted={sorted("fullName")} onClick={() => changeSort("fullName")}>Instalador</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("assignedOrders")} onClick={() => changeSort("assignedOrders")} textAlign="right">Con actividad</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("completedOrders")} onClick={() => changeSort("completedOrders")} textAlign="right">Completadas</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("incompleteOrders")} onClick={() => changeSort("incompleteOrders")} textAlign="right">Inconclusas</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("scheduledOrders")} onClick={() => changeSort("scheduledOrders")} textAlign="right">Agendadas</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("overdueInstallations")} onClick={() => changeSort("overdueInstallations")} textAlign="right">Vencidas</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("ordersWithGuarantee")} onClick={() => changeSort("ordersWithGuarantee")} textAlign="right">Terminados con garantía</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("completionRatePercent")} onClick={() => changeSort("completionRatePercent")} textAlign="right">Cierre / actividad</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("guaranteeIncidencePercent")} onClick={() => changeSort("guaranteeIncidencePercent")} textAlign="right">Incidencia</Table.HeaderCell>
            </Table.Row>
        );
        if (role === "LOGISTIC") return (
            <Table.Row>
                <Table.HeaderCell sorted={sorted("fullName")} onClick={() => changeSort("fullName")}>Responsable</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("receivedTransitions")} onClick={() => changeSort("receivedTransitions")} textAlign="right">Recibidos</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("readyToScheduleTransitions")} onClick={() => changeSort("readyToScheduleTransitions")} textAlign="right">Listos para agendar</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("scheduledTransitions")} onClick={() => changeSort("scheduledTransitions")} textAlign="right">Agendados</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("completedTransitions")} onClick={() => changeSort("completedTransitions")} textAlign="right">Concluidos</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("incompleteTransitions")} onClick={() => changeSort("incompleteTransitions")} textAlign="right">Inconclusos</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("successfulClosureRatePercent")} onClick={() => changeSort("successfulClosureRatePercent")} textAlign="right">Cierre satisfactorio</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("pendingInWarehouse")} onClick={() => changeSort("pendingInWarehouse")} textAlign="right">En almacén</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("pendingToSchedule")} onClick={() => changeSort("pendingToSchedule")} textAlign="right">Por agendar</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("medianHoursWarehouseToReady")} onClick={() => changeSort("medianHoursWarehouseToReady")} textAlign="right">Almacén → listo</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("medianHoursReadyToScheduled")} onClick={() => changeSort("medianHoursReadyToScheduled")} textAlign="right">Listo → agendado</Table.HeaderCell>
            </Table.Row>
        );
        return (
            <Table.Row>
                <Table.HeaderCell sorted={sorted("fullName")} onClick={() => changeSort("fullName")}>Usuario</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("createdOrders")} onClick={() => changeSort("createdOrders")} textAlign="right">Creados</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("validationTransitions")} onClick={() => changeSort("validationTransitions")} textAlign="right">A validación</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("productionTransitions")} onClick={() => changeSort("productionTransitions")} textAlign="right">A producción</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("pendingDraft")} onClick={() => changeSort("pendingDraft")} textAlign="right">Borradores</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("pendingValidation")} onClick={() => changeSort("pendingValidation")} textAlign="right">Por producir</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("medianHoursDraftToValidation")} onClick={() => changeSort("medianHoursDraftToValidation")} textAlign="right">Borrador → validación</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("medianHoursValidationToProduction")} onClick={() => changeSort("medianHoursValidationToProduction")} textAlign="right">Validación → producción</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("externalUpdates")} onClick={() => changeSort("externalUpdates")} textAlign="right">Actualizaciones</Table.HeaderCell>
                <Table.HeaderCell sorted={sorted("ordersTouched")} onClick={() => changeSort("ordersTouched")} textAlign="right">Pedidos atendidos</Table.HeaderCell>
            </Table.Row>
        );
    };

    const renderRows = () => data?.users.map((user) => {
        if (role === "ADVISOR") return (
            <Table.Row key={user.userId}>
                <Table.Cell>{userCell(user)}</Table.Cell>
                {metricCell(user, "PROSPECTS", "Prospectos asignados", formatNumber(user.funnel?.prospects), formatNumber(user.funnel?.prospects))}
                {metricCell(user, "CONVERTED_PROSPECTS", "Vendidos al corte", formatNumber(user.funnel?.convertedProspects), formatNumber(user.funnel?.convertedProspects))}
                {metricCell(user, "CONVERSION_RATE", "Conversión", <Label basic>{formatPercent(user.funnel?.conversionRatePercent)}</Label>, formatPercent(user.funnel?.conversionRatePercent))}
                {metricCell(user, "AVERAGE_DAYS_TO_SALE", "Días a venta", Number(user.funnel?.averageDaysToSale || 0).toFixed(1), `${Number(user.funnel?.averageDaysToSale || 0).toFixed(1)} días`)}
                {metricCell(user, "ACTIVE_ORDERS", "Pedidos activos", formatNumber(user.advisor?.activeOrders), formatNumber(user.advisor?.activeOrders))}
                {metricCell(user, "ATTENTION_ORDERS", "Pedidos por atender", formatNumber(user.advisor?.attentionOrders), formatNumber(user.advisor?.attentionOrders))}
                {metricCell(user, "SALES_AMOUNT", "Valor de pedidos creados", formatCurrency(user.advisor?.salesAmount), formatCurrency(user.advisor?.salesAmount))}
                {metricCell(user, "COLLECTED_AMOUNT", "Cobrado acumulado", formatCurrency(user.advisor?.collectedAmount), formatCurrency(user.advisor?.collectedAmount))}
                {metricCell(user, "ACTIVE_OUTSTANDING_BALANCE", "Saldo activo", formatCurrency(user.advisor?.activeOutstandingBalance), formatCurrency(user.advisor?.activeOutstandingBalance))}
                {metricCell(user, "COLLECTION_RATE", "Cobranza", <strong>{formatPercent(user.advisor?.collectionRatePercent)}</strong>, formatPercent(user.advisor?.collectionRatePercent))}
            </Table.Row>
        );
        if (role === "SOCIAL_MEDIA") return (
            <Table.Row key={user.userId}>
                <Table.Cell>{userCell(user)}</Table.Cell>
                {metricCell(user, "PROSPECTS", "Prospectos creados", formatNumber(user.funnel?.prospects), formatNumber(user.funnel?.prospects))}
                {metricCell(user, "CONVERTED_PROSPECTS", "Convertidos", formatNumber(user.funnel?.convertedProspects), formatNumber(user.funnel?.convertedProspects))}
                {metricCell(user, "LOST_PROSPECTS", "Perdidos", formatNumber(user.funnel?.lostProspects), formatNumber(user.funnel?.lostProspects))}
                {metricCell(user, "OPEN_PROSPECTS", "Abiertos", formatNumber(user.funnel?.openProspects), formatNumber(user.funnel?.openProspects))}
                {metricCell(user, "CONVERSION_RATE", "Conversión", <Label basic>{formatPercent(user.funnel?.conversionRatePercent)}</Label>, formatPercent(user.funnel?.conversionRatePercent))}
                {metricCell(user, "AVERAGE_DAYS_TO_SALE", "Días a venta", Number(user.funnel?.averageDaysToSale || 0).toFixed(1), `${Number(user.funnel?.averageDaysToSale || 0).toFixed(1)} días`)}
            </Table.Row>
        );
        if (role === "INSTALLER") return (
            <Table.Row key={user.userId}>
                <Table.Cell>{userCell(user)}</Table.Cell>
                {metricCell(user, "ASSIGNED_ORDERS", "Pedidos con actividad", formatNumber(user.installer?.assignedOrders), formatNumber(user.installer?.assignedOrders))}
                {metricCell(user, "COMPLETED_ORDERS", "Completadas", formatNumber(user.installer?.completedOrders), formatNumber(user.installer?.completedOrders))}
                {metricCell(user, "INCOMPLETE_ORDERS", "Inconclusas", formatNumber(user.installer?.incompleteOrders), formatNumber(user.installer?.incompleteOrders))}
                {metricCell(user, "SCHEDULED_ORDERS", "Agendadas", formatNumber(user.installer?.scheduledOrders), formatNumber(user.installer?.scheduledOrders))}
                {metricCell(user, "OVERDUE_INSTALLATIONS", "Vencidas", formatNumber(user.installer?.overdueInstallations), formatNumber(user.installer?.overdueInstallations))}
                {metricCell(user, "ORDERS_WITH_GUARANTEE", "Terminados con garantía", formatNumber(user.installer?.ordersWithGuarantee), formatNumber(user.installer?.ordersWithGuarantee))}
                {metricCell(user, "COMPLETION_RATE", "Cierre sobre actividad", <strong>{formatPercent(user.installer?.completionRatePercent)}</strong>, formatPercent(user.installer?.completionRatePercent))}
                {metricCell(user, "GUARANTEE_INCIDENCE", "Incidencia", formatPercent(user.installer?.guaranteeIncidencePercent), formatPercent(user.installer?.guaranteeIncidencePercent))}
            </Table.Row>
        );
        if (role === "LOGISTIC") return (
            <Table.Row key={user.userId}>
                <Table.Cell>{userCell(user)}</Table.Cell>
                {metricCell(user, "RECEIVED_TRANSITIONS", "Recibidos", formatNumber(user.logistics?.receivedTransitions), formatNumber(user.logistics?.receivedTransitions))}
                {metricCell(user, "READY_TO_SCHEDULE_TRANSITIONS", "Listos para agendar", formatNumber(user.logistics?.readyToScheduleTransitions), formatNumber(user.logistics?.readyToScheduleTransitions))}
                {metricCell(user, "SCHEDULED_TRANSITIONS", "Agendados", <strong>{formatNumber(user.logistics?.scheduledTransitions)}</strong>, formatNumber(user.logistics?.scheduledTransitions))}
                {metricCell(user, "COMPLETED_TRANSITIONS", "Concluidos", formatNumber(user.logistics?.completedTransitions), formatNumber(user.logistics?.completedTransitions))}
                {metricCell(user, "INCOMPLETE_TRANSITIONS", "Inconclusos", formatNumber(user.logistics?.incompleteTransitions), formatNumber(user.logistics?.incompleteTransitions))}
                {metricCell(user, "SUCCESSFUL_CLOSURE_RATE", "Cierre satisfactorio", <strong>{formatPercent(user.logistics?.successfulClosureRatePercent)}</strong>, formatPercent(user.logistics?.successfulClosureRatePercent))}
                {metricCell(user, "PENDING_IN_WAREHOUSE", "En almacén", formatNumber(user.logistics?.pendingInWarehouse), formatNumber(user.logistics?.pendingInWarehouse))}
                {metricCell(user, "PENDING_TO_SCHEDULE", "Por agendar", formatNumber(user.logistics?.pendingToSchedule), formatNumber(user.logistics?.pendingToSchedule))}
                {metricCell(user, "WAREHOUSE_TO_READY_TIME", "Almacén a listo", <><strong>{Number(user.logistics?.medianHoursWarehouseToReady || 0).toFixed(1)} h</strong><small>P90 {Number(user.logistics?.p90HoursWarehouseToReady || 0).toFixed(1)} h · n={formatNumber(user.logistics?.warehouseToReadySamples)}</small></>, `${Number(user.logistics?.medianHoursWarehouseToReady || 0).toFixed(1)} h`)}
                {metricCell(user, "READY_TO_SCHEDULED_TIME", "Listo a agendado", <><strong>{Number(user.logistics?.medianHoursReadyToScheduled || 0).toFixed(1)} h</strong><small>P90 {Number(user.logistics?.p90HoursReadyToScheduled || 0).toFixed(1)} h · n={formatNumber(user.logistics?.readyToScheduledSamples)}</small></>, `${Number(user.logistics?.medianHoursReadyToScheduled || 0).toFixed(1)} h`)}
            </Table.Row>
        );
        return (
            <Table.Row key={user.userId}>
                <Table.Cell>{userCell(user)}</Table.Cell>
                {metricCell(user, "CREATED_ORDERS", "Pedidos creados", formatNumber(user.operations?.createdOrders), formatNumber(user.operations?.createdOrders))}
                {metricCell(user, "VALIDATION_TRANSITIONS", "Pasados a validación", formatNumber(user.operations?.validationTransitions), formatNumber(user.operations?.validationTransitions))}
                {metricCell(user, "PRODUCTION_TRANSITIONS", "Enviados a producción", formatNumber(user.operations?.productionTransitions), formatNumber(user.operations?.productionTransitions))}
                {metricCell(user, "PENDING_DRAFT", "Pendientes en borrador", formatNumber(user.operations?.pendingDraft), formatNumber(user.operations?.pendingDraft))}
                {metricCell(user, "PENDING_VALIDATION", "Pendientes de producción", formatNumber(user.operations?.pendingValidation), formatNumber(user.operations?.pendingValidation))}
                {metricCell(user, "DRAFT_TO_VALIDATION_TIME", "Borrador a validación", <><strong>{Number(user.operations?.medianHoursDraftToValidation || 0).toFixed(1)} h</strong><small>P90 {Number(user.operations?.p90HoursDraftToValidation || 0).toFixed(1)} h · n={formatNumber(user.operations?.draftToValidationSamples)}</small></>, `${Number(user.operations?.medianHoursDraftToValidation || 0).toFixed(1)} h`)}
                {metricCell(user, "VALIDATION_TO_PRODUCTION_TIME", "Validación a producción", <><strong>{Number(user.operations?.medianHoursValidationToProduction || 0).toFixed(1)} h</strong><small>P90 {Number(user.operations?.p90HoursValidationToProduction || 0).toFixed(1)} h · n={formatNumber(user.operations?.validationToProductionSamples)}</small></>, `${Number(user.operations?.medianHoursValidationToProduction || 0).toFixed(1)} h`)}
                {metricCell(user, "EXTERNAL_UPDATES", "Actualizaciones externas", formatNumber(user.operations?.externalUpdates), formatNumber(user.operations?.externalUpdates))}
                {metricCell(user, "ORDERS_TOUCHED", "Pedidos con seguimiento", formatNumber(user.operations?.ordersTouched), formatNumber(user.operations?.ordersTouched))}
            </Table.Row>
        );
    });

    return (
        <div className="dashboard-section">
            <div className="dashboard-section-heading">
                <div>
                    <span className="dashboard-eyebrow">Desempeño por rol</span>
                    <h2>Resultados controlables por cada equipo</h2>
                    <p>La comparación cambia de indicadores según la responsabilidad del rol.</p>
                </div>
            </div>

            <Menu pointing secondary stackable className="dashboard-role-tabs">
                {roles.map((item) => (
                    <Menu.Item key={item.role} active={role === item.role} onClick={() => selectRole(item.role)}>
                        <Icon name={item.icon} /> {item.label}
                    </Menu.Item>
                ))}
            </Menu>

            <Segment>
                <div className="dashboard-section-heading dashboard-section-heading--compact">
                    <div>
                        <Header as="h3" content={roleConfig.label} />
                        <p>{roleConfig.description}</p>
                    </div>
                    <Form className="dashboard-table-filters" onSubmit={() => { setPage(0); setSearch(searchInput.trim()); }}>
                        <Input
                            icon="search"
                            placeholder="Buscar usuario"
                            value={searchInput}
                            onChange={(_, input) => setSearchInput(String(input.value))}
                            action={{ icon: "search", type: "submit" }}
                        />
                        <Dropdown
                            selection
                            clearable
                            placeholder="Todos"
                            value={active === undefined ? "" : String(active)}
                            options={[
                                { key: "active", value: "true", text: "Activos" },
                                { key: "inactive", value: "false", text: "Inactivos" },
                            ]}
                            onChange={(_, value) => {
                                setPage(0);
                                const selected = value.value;
                                setActive(selected === "" || selected == null
                                    ? undefined
                                    : selected === "true");
                            }}
                        />
                    </Form>
                </div>

                {data && (
                    <Message
                        size="mini"
                        info
                        content={role === "ADVISOR"
                            ? "El embudo usa la creación del prospecto; pedidos y cobranza usan la creación del pedido. Ambos resultados se confirman al corte."
                            : data.periodBasis === "PROSPECT_CREATED_AT"
                            ? "El periodo define la cohorte por fecha de creación del prospecto; la conversión se confirma al corte."
                            : data.periodBasis === "EXTERNAL_UPDATE_CREATED_AT"
                                ? "El periodo se aplica a la fecha de cada actualización externa, incluso si el pedido fue creado antes."
                                : "El periodo se aplica a la fecha de las transiciones; los pendientes son una foto vigente al corte."}
                    />
                )}

                {role === "LOGISTIC" && data?.logisticsCoverage && (
                    <Message
                        size="mini"
                        warning={data.logisticsCoverage.unattributedTransitions > 0}
                        header={`Cobertura de atribución: ${formatPercent(data.logisticsCoverage.userAttributionCoveragePercent)}`}
                        content={`${formatNumber(data.logisticsCoverage.logisticsAttributedTransitions)} transiciones atribuidas a Logística, ${formatNumber(data.logisticsCoverage.otherRoleAttributedTransitions)} a otros roles y ${formatNumber(data.logisticsCoverage.unattributedTransitions)} sin usuario, de ${formatNumber(data.logisticsCoverage.relevantTransitions)} eventos del periodo. Backlog sin responsable: ${formatNumber(data.logisticsCoverage.pendingInWarehouseWithoutUser)} en almacén y ${formatNumber(data.logisticsCoverage.pendingToScheduleWithoutUser)} por agendar.`}
                    />
                )}

                {loading && <Loader active inline="centered">Consultando réplica...</Loader>}
                {!loading && data?.users.length === 0 && <Message info content="No hay usuarios para los filtros seleccionados." />}
                {!loading && Boolean(data?.users.length) && (
                    <div className="dashboard-table-scroll">
                        <Table sortable striped compact selectable className="dashboard-table dashboard-performance-table">
                            <Table.Header>{renderHeaders()}</Table.Header>
                            <Table.Body>{renderRows()}</Table.Body>
                        </Table>
                    </div>
                )}

                <div className="dashboard-pagination dashboard-pagination--split">
                    <span>{formatNumber(data?.totalElements)} colaboradores</span>
                    <Pagination
                        activePage={page + 1}
                        totalPages={Math.max(data?.totalPages || 1, 1)}
                        onPageChange={(_, pagination) => setPage(Number(pagination.activePage) - 1)}
                    />
                    <Dropdown
                        compact
                        selection
                        value={size}
                        options={[10, 25, 50, 100].map((value) => ({ key: value, value, text: `${value} filas` }))}
                        onChange={(_, value) => { setPage(0); setSize(Number(value.value)); }}
                    />
                </div>
            </Segment>
            <RolePerformanceDrilldownModal
                selection={selection}
                filters={filters}
                onClose={() => setSelection(undefined)}
            />
        </div>
    );
};

export default RolePerformance;
